import React, { useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { FiTrash2 } from "react-icons/fi";
import { IKImage, IKContext, IKUpload } from "imagekitio-react";
import {
  urlEndpoint,
  authEndpoint,
  pubKeyImageKit,
} from "../../../middlewares/libraries/imageKit/ImageKit";
import GenerateImage from "../../../pages/doseeData/utils/GenerateImage";
import { MyButton } from "../../../pages/home/component/ButtonUtils";

// --- Inline SVG replacement for "Sparkles" ---
const SparklesIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    {/* main sparkle */}
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.75l-1.06 2.121a.75.75 0 01-1.346 0L6.349 15.75l-2.121-1.06a.75.75 0 010-1.346l2.121-1.06 1.058-2.122a.75.75 0 011.346 0l1.06 2.122 2.121 1.06a.75.75 0 010 1.346L9.813 15.75z"
    />
    {/* small sparkles */}
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17.813 8.25l-.53 1.061a.375.375 0 01-.673 0l-.53-1.061-1.06-.53a.375.375 0 010-.673l1.06-.53.53-1.061a.375.375 0 01.673 0l.53 1.06 1.061.531a.375.375 0 010 .673l-1.061.53z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20.813 15.75l-.53 1.061a.375.375 0 01-.673 0l-.53-1.061-1.06-.53a.375.375 0 010-.673l1.06-.53.53-1.061a.375.375 0 01.673 0l.53 1.06 1.061.531a.375.375 0 010 .673l-1.061.53z"
    />
  </svg>
);

export const ImageKitUpload = ({
  folder,
  thumbnail,
  onSuccess,
  thumbnailId,
  setThumbnail,
  setThumbnailId,
  isRestaurantModal = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(null);

  const MIN_H = 160;
  const MAX_H = 220;

  return (
    <>
      {showImageModal ? (
        <GenerateImage
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          setShowImageModal={setShowImageModal}
        />
      ) : null}

      <div className="w-full">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Thumbnail</h3>
              <p className="mt-1 text-[11px] text-gray-500">
                JPG / PNG / WEBP • Min 800×600 • Max 5 MB
              </p>
            </div>
          </div>

          <div className="p-5">
            <IKContext
              urlEndpoint={urlEndpoint}
              publicKey={pubKeyImageKit}
              authenticationEndpoint={authEndpoint}
            >
              <div
                className="group relative mx-auto w-full max-w-xl overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                style={{ height: `clamp(${MIN_H}px, 24vw, ${MAX_H}px)` }}
              >
                {thumbnail ? (
                  <>
                    <IKImage
                      src={thumbnail}
                      width="1000"
                      alt="Selected thumbnail"
                      className="h-full w-full object-cover"
                    />
                    {thumbnailId ? (
                      <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-1 text-[10px] font-mono text-white">
                        ID: {thumbnailId.slice(0, 10)}…
                      </span>
                    ) : null}
                    <div className="pointer-events-none absolute inset-0 hidden items-center justify-center bg-white/30 backdrop-blur-[1px] group-hover:flex">
                      <span className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-700 shadow-sm">
                        Replace image
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <div className="mx-auto mb-2 h-12 w-12 rounded-full border border-dashed border-gray-300" />
                      <p className="text-xs font-medium text-gray-600">No image selected</p>
                      <p className="mt-1 text-[11px] text-gray-500">Upload or generate a new image</p>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                    <AiOutlineLoading className="h-6 w-6 animate-spin text-gray-800" />
                  </div>
                ) : null}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <IKUpload
                  fileName="image-upload.png"
                  folder={folder}
                  accept=".jpg,.jpeg,.png,.webp"
                  onError={() => setLoading(false)}
                  onChange={() => setLoading(true)}
                  onClick={() => {
                    setLoading(false);
                    setThumbnail(null);
                    setThumbnailId(null);
                  }}
                  onSuccess={(e) => {
                    setLoading(false);
                    setThumbnail(e.url);
                    setThumbnailId(e.fileId);
                    if (isRestaurantModal && onSuccess) onSuccess(e.url, e.fileId);
                  }}
                  className={
                    `flex-1 sm:flex-none file:cursor-pointer file:rounded-lg file:border file:border-gray-200 file:bg-white file:px-4 file:py-2.5 file:text-xs file:font-semibold file:text-gray-700 file:transition file:hover:bg-gray-50 ` +
                    (loading ? `hidden` : `block`)
                  }
                />

                <MyButton
                  onClick={() => setShowImageModal(true)}
                  className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold rounded-lg px-3 py-2.5"
                >
                  <SparklesIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Generate Image</span>
                  <span className="sm:hidden">Generate</span>
                </MyButton>

                {thumbnail ? (
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnail(null);
                      setThumbnailId(null);
                    }}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    title="Remove image"
                  >
                    <FiTrash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Remove</span>
                  </button>
                ) : null}
              </div>
            </IKContext>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-gray-700">
                High contrast
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-gray-700">
                Landscape
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-gray-700">
                16:9 or 4:3
              </span>
              <p className="ml-auto text-[11px] text-gray-500">
                Tip: Bright, high-contrast images perform better in lists.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
